/**
 * Documents-tab 'Download zip' client (D-2). POSTs the selected (document, column)
 * cells plus optional IAA Parties/Date params; returns the ZIP blob and the
 * skipped-items summary carried in the X-Zip-Skipped* headers. The 'Email' action hands
 * the same selection to the record Email dialog (P34, api/emailDrafts.ts).
 *
 * A 409 iaa_export_blocked is re-thrown as the shared IaaExportBlockedError so
 * the IAA modal can render the same per-person missing-field list it uses for
 * the per-file export.
 */
import { IaaExportBlockedError, type IaaExportMissingRecord, downloadBlob } from './iaaExports';
import type { ISODate, UUID } from './types';

export { downloadBlob };

export interface DocumentsZipItem {
  /** 'client' = the client record of each person in `client_record.person_ids` (PDF). */
  document: 'iaa' | 'fees' | 'aq' | 'pp' | 'samplemonthly' | 'client';
  column: 'fillable' | 'pt' | 'en';
  /** Output format for a Filled cell (Legal-docs PDF arc Inc 1). Only Annex I
   * (fees) honours 'pdf' this increment; others ignore it. Defaults to 'word'. */
  format?: 'word' | 'pdf';
}

export interface DocumentsZipIaa {
  parties: { party_id: UUID; position?: string | null }[];
  document_date?: ISODate;
}

/** One schedule's fee simulation for the bundle's Annex I PDF(s) (Pedro 2026-10-10/11);
 *  frequencies and VAT are the accounts' own. The Word Annex I stays standard. */
export interface DocumentsZipFeeExample {
  schedule_id: UUID;
  amount_eur: number;
  /** Chosen next to the amount (Pedro's smoke 2026-10-11); omitted = the accounts' own. */
  frequency?: 'monthly' | 'quarterly' | 'semiannual';
}

export interface DocumentsZipRequestBody {
  items: DocumentsZipItem[];
  iaa?: DocumentsZipIaa;
  fee_examples?: DocumentsZipFeeExample[];
  /** Whose client record(s) to include (parties of the agreement); needed with a 'client' item. */
  client_record?: { person_ids: UUID[] };
}

export interface DocumentsZipResult {
  blob: Blob;
  fileName: string;
  skipped: number;
  skippedDetail: string | null;
}

export async function downloadDocumentsZip(
  agreementId: UUID,
  body: DocumentsZipRequestBody,
): Promise<DocumentsZipResult> {
  const res = await postSelection(`/agreements/${agreementId}/documents/zip`, body,
    'The document bundle could not be generated.');
  const cd = res.headers.get('Content-Disposition') ?? '';
  const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
  const fileName = m ? decodeURIComponent(m[1]) : 'documents.zip';
  const skippedRaw = res.headers.get('X-Zip-Skipped');
  const detailRaw = res.headers.get('X-Zip-Skipped-Detail');
  return {
    blob: await res.blob(),
    fileName,
    skipped: skippedRaw ? parseInt(skippedRaw, 10) : 0,
    skippedDetail: detailRaw ? decodeURIComponent(detailRaw) : null,
  };
}

/** POST a Documents-tab selection; throws the IAA-blocked error (rendered in the IAA dialog)
 *  or an Error with the server's message. */
async function postSelection(url: string, body: DocumentsZipRequestBody, fallback: string): Promise<Response> {
  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail: unknown = null;
    try { detail = (await res.json()).detail; } catch { /* non-JSON error body */ }
    if (
      res.status === 409 && detail && typeof detail === 'object'
      && (detail as { code?: string }).code === 'iaa_export_blocked'
    ) {
      const d = detail as { message: string; missing: IaaExportMissingRecord[] };
      throw new IaaExportBlockedError(d.message, d.missing);
    }
    const msg = detail && typeof detail === 'object' && 'message' in detail
      ? String((detail as { message: unknown }).message)
      : (typeof detail === 'string' ? detail : fallback);
    throw new Error(msg);
  }
  return res;
}
